<template>
  <a-modal :visible="visible" :title="paymentTitle" :footer="null" :maskClosable="false" @cancel="handleCancel">
    <div class="payment-modal-content">
      <div class="order-info">
        <div class="order-item">
          <span class="order-label">套餐名称：</span>
          <a-tooltip placement="topLeft" :title="packageInfo?.name">
            <span class="order-value text-ellipsis">{{ packageInfo?.name || '--' }}</span>
          </a-tooltip>
        </div>
        <div class="order-item">
          <span class="order-label">订单编号：</span>
          <a-tooltip placement="topLeft" :title="currentOrderNumber">
            <span class="order-value text-ellipsis">{{ currentOrderNumber || '--' }}</span>
          </a-tooltip>
        </div>
        <div class="order-item">
          <span class="order-label">存储容量：</span>
          <span class="order-value">{{ $formatCapacity(packageInfo?.capacity) }}</span>
        </div>
        <div class="order-item">
          <span class="order-label">有效期：</span>
          <span class="order-value">{{ getValidityText(packageInfo) }}</span>
        </div>

        <div class="order-item">
          <span class="order-label">优惠券：</span>
          <div class="coupon-section">
            <a-button type="link" @click="showCouponList" class="coupon-btn" :disabled="loading || paymentSuccess">
              <a-tooltip placement="topLeft" :title="selectedCoupon ? selectedCoupon.couponName : '选择优惠券'">
                <span class="text-ellipsis">{{
                  selectedCoupon ? `已选优惠券 (${selectedCoupon.couponName})` : '选择优惠券'
                }}</span>
              </a-tooltip>
            </a-button>
            <a-button
              v-if="selectedCoupon"
              type="link"
              size="small"
              @click="clearCoupon"
              :disabled="loading || paymentSuccess"
              class="clear-coupon-btn">
              取消
            </a-button>
          </div>
        </div>

        <div class="order-item">
          <span class="order-label">支付金额：</span>
          <div class="price-section">
            <a-tooltip placement="topLeft" :title="`原价：¥${packageInfo?.price || '0.00'}`">
              <span v-if="selectedCoupon" class="original-price">¥{{ packageInfo?.price || '0.00' }}</span>
            </a-tooltip>
            <span class="order-price">¥{{ actualPrice || '0.00' }}</span>
            <a-tooltip placement="topLeft" :title="`优惠：¥${discountAmount}`">
              <span v-if="selectedCoupon" class="discount-amount">立省 ¥{{ discountAmount }}</span>
            </a-tooltip>
          </div>
        </div>
      </div>

      <!-- 生成二维码按钮区域 -->
      <div class="generate-qr-section" v-if="!qrCodeGenerated && !paymentSuccess && !paymentError">
        <a-button type="primary" size="large" @click="generateQRCode" :loading="loading" class="generate-qr-btn">
          {{ loading ? '正在生成...' : '生成支付二维码' }}
        </a-button>
        <div class="generate-tip">确认订单信息后点击生成支付二维码</div>
        <div v-if="selectedCoupon" class="coupon-applied-tip">
          <a-icon type="check-circle" style="color: #52c41a; margin-right: 4px" />
          已应用优惠券：{{ selectedCoupon.couponName }}
        </div>
      </div>

      <!-- 二维码显示区域 -->
      <div class="qr-code-section" v-if="qrCodeGenerated && !paymentSuccess && !paymentError">
        <div class="qr-code-header">
          <div class="qr-code-title">请使用微信扫描二维码完成支付</div>
          <a-button type="link" @click="regenerateQRCode" :disabled="loading" class="regenerate-btn">
            <a-icon type="reload" />
            重新生成二维码
          </a-button>
        </div>
        <div class="qr-code-container">
          <div class="qr-code-placeholder">
            <div v-if="loading" class="loading-container">
              <div class="loading-spinner"></div>
              <div class="loading-text">正在生成支付二维码...</div>
            </div>

            <div v-else class="qr-code-image">
              <vue-qr
                :text="downloadData.url"
                :margin="0"
                colorDark="#333"
                colorLight="#fff"
                :logoSrc="downloadData.icon + '?t=' + new Date().getTime()"
                :logoScale="0.2"
                :size="200"
                style="width: 200px; height: 200px;"></vue-qr>
            </div>
          </div>
        </div>
        <div class="qr-code-tip">
          二维码有效时间：<span class="time-count">{{ countdown }}</span>
        </div>
        <div v-if="selectedCoupon" class="coupon-info-tip">
          当前订单已应用优惠券：<span class="coupon-highlight">{{ selectedCoupon.couponName }}</span>
          <a-button type="link" size="small" @click="showCouponList" class="change-coupon-btn">修改优惠券</a-button>
        </div>
        <div v-else class="coupon-info-tip">
          <a-button type="link" size="small" @click="showCouponList" class="change-coupon-btn">选择优惠券</a-button>
        </div>
      </div>

      <!-- 错误状态 -->
      <div class="error-section" v-if="paymentError">
        <div class="error-container">
          <div class="error-icon">⚠️</div>
          <div class="error-text">生成支付二维码失败</div>
          <a-tooltip placement="topLeft" :title="errorMessage">
            <div class="error-desc text-ellipsis">{{ errorMessage }}</div>
          </a-tooltip>
          <a-button type="primary" @click="retryCreateOrder" class="retry-btn">重新生成</a-button>
        </div>
      </div>

      <!-- 支付成功状态 -->
      <div class="success-section" v-if="paymentSuccess">
        <div class="payment-success">
          <div class="success-icon">✓</div>
          <div class="success-text">支付成功</div>
          <div class="success-desc">系统正在为您开通服务，请稍候...</div>
        </div>
      </div>

      <div class="payment-tips" v-if="qrCodeGenerated && !paymentError">
        <div class="tip-item">
          <span>支付成功后，系统将自动为您开通服务</span>
        </div>
        <div class="tip-item">
          <span>如有问题，请联系客服：{{ initPassword || '' }}</span>
        </div>
      </div>
    </div>

    <a-drawer title="选择优惠券" :visible="couponDrawerVisible" :width="600" @close="closeCouponDrawer" destroyOnClose>
      <div class="coupon-drawer-content">
        <div class="coupon-stats">
          <div class="coupon-count-section">
            <div v-if="qrCodeGenerated" class="coupon-change-warning">
              <a-icon type="exclamation-circle" style="color: #faad14; margin-right: 4px" />
              修改优惠券需要重新生成支付二维码
            </div>
          </div>
        </div>

        <div class="coupon-list">
          <div
            v-for="coupon in filteredCoupons"
            :key="coupon.id"
            class="coupon-item"
            :class="{
              'coupon-item-selected': selectedCoupon?.id === coupon.id,
              'coupon-item-used': coupon.status === 2,
              'coupon-item-expired': coupon.status === 0,
              'coupon-item-unavailable': !isCouponAvailable(coupon) && coupon.status === 1,
            }"
            @click="selectCoupon(coupon)">
            <div class="coupon-left">
              <div class="coupon-amount">
                <div class="coupon-value" v-if="coupon.couponType === 10">
                  <span class="currency">¥</span>
                  <span class="amount">{{ coupon.reduceAmount }}</span>
                </div>
                <div class="coupon-value" v-else-if="coupon.couponType === 20">
                  <span class="amount">{{ (coupon.couponDiscount * 10).toFixed(1) }}</span>
                  <span class="unit">折</span>
                </div>
                <div class="coupon-value" v-else-if="coupon.couponType === 30">
                  <span class="amount">免单</span>
                </div>
              </div>
              <div class="coupon-condition" v-if="coupon.cashCondition > 0">满{{ coupon.cashCondition }}元可用</div>
              <div class="coupon-condition" v-else>无门槛</div>
            </div>

            <div class="coupon-info">
              <div class="coupon-main-info">
                <a-tooltip placement="topLeft" :title="coupon.couponName">
                  <div class="coupon-name text-ellipsis">{{ coupon.couponName }}</div>
                </a-tooltip>
                <a-tooltip placement="topLeft" :title="coupon.subTitle">
                  <div class="coupon-subtitle text-ellipsis">{{ coupon.subTitle }}</div>
                </a-tooltip>
              </div>
              <div class="coupon-secondary-info">
                <div class="coupon-validity">
                  {{ formatDate(coupon.userStartTime) }} - {{ formatDate(coupon.userEndTime) }}
                </div>
                <div class="coupon-module">
                  <a-tag :color="getWhTypeColor(coupon.whType)" size="small">
                    {{ getWhTypeText(coupon.whType) }}
                  </a-tag>
                </div>
              </div>
            </div>

            <div class="coupon-check">
              <a-icon
                type="check-circle"
                :style="{
                  color: selectedCoupon?.id === coupon.id ? '#52c41a' : '#d9d9d9',
                  fontSize: '20px',
                }" />
            </div>

            <div class="coupon-status-tag" v-if="coupon.status !== 1 || !isCouponAvailable(coupon)">
              <a-tag :color="coupon.status === 0 ? 'red' : coupon.status === 2 ? 'orange' : 'default'" size="small">
                {{
                  coupon.status === 0 ? '已失效' :
                  coupon.status === 2 ? '已使用' :
                  !isCouponAvailable(coupon) ? '不满足条件' : ''
                }}
              </a-tag>
            </div>
          </div>

          <div v-if="filteredCoupons.length === 0 && !couponLoading" class="no-coupon">
            <a-empty description="暂无可用优惠券" />
          </div>

          <div v-if="couponLoading" class="coupon-loading">
            <a-spin size="large" />
            <div class="loading-text">加载优惠券中...</div>
          </div>
        </div>
      </div>

      <div
        :style="{
          position: 'absolute',
          right: 0,
          bottom: 0,
          width: '100%',
          borderTop: '1px solid #e9e9e9',
          padding: '10px 16px',
          background: '#fff',
          textAlign: 'right',
          zIndex: 1,
        }">
        <a-button style="margin-right: 8px" @click="closeCouponDrawer">取消</a-button>
        <a-button type="primary" @click="confirmCouponSelection" :disabled="!selectedCoupon">确定</a-button>
      </div>
    </a-drawer>
  </a-modal>
</template>

<script>
import vueQr from 'vue-qr'
import { creatOrder, checkPayStatus, creatOrderqiye } from '@/api/VR/storagePackage'
import { getTenantAvailableCoupons } from '@/api/coupon/controller'

export default {
  name: 'PaymentModal',
  components: {
    vueQr,
  },
  data() {
    return {
      visible: false,
      paying: false,
      loading: false,
      paymentSuccess: false,
      paymentError: false,
      errorMessage: '',
      paymentMethod: 'wechat',
      countdown: '05:00',
      timer: null,
      pollingTimer: null,
      packageInfo: null,
      currentOrderId: null,
      currentOrderNumber: null,
      downloadData: {
        url: '',
        icon: 'http://hoy-test-1308702491.cos.ap-beijing.myqcloud.com/hoy/oth/IMG_20251107_162000__020544.png',
      },
      initPassword: '',
      couponDrawerVisible: false,
      availableCoupons: [],
      selectedCoupon: null,
      couponLoading: false,

      hasAutoSelectedCoupon: false,
      qrCodeGenerated: false, // 控制二维码是否已生成
    }
  },
  computed: {
    paymentTitle() {
      const packageName = this.packageInfo?.name || '未知套餐'
      return `支付订单 - ${packageName}`
    },
    actualPrice() {
      const originalPrice = parseFloat(this.packageInfo?.price) || 0
      if (this.selectedCoupon && this.selectedCoupon.status === 1) {
        if (this.selectedCoupon.cashCondition > 0 && originalPrice < this.selectedCoupon.cashCondition) {
          return originalPrice.toFixed(2)
        }

        let discount = 0
        if (this.selectedCoupon.couponType === 10) {
          discount = parseFloat(this.selectedCoupon.reduceAmount) || 0
        } else if (this.selectedCoupon.couponType === 20) {
          const discountRate = parseFloat(this.selectedCoupon.couponDiscount) || 0
          discount = originalPrice * (1 - discountRate)
        } else if (this.selectedCoupon.couponType === 30) {
          discount = originalPrice
        }
        const finalPrice = Math.max(originalPrice - discount, 0)
        return finalPrice.toFixed(2)
      }
      return originalPrice.toFixed(2)
    },
    discountAmount() {
      const originalPrice = parseFloat(this.packageInfo?.price) || 0
      const actualPrice = parseFloat(this.actualPrice) || 0
      return (originalPrice - actualPrice).toFixed(2)
    },

    filteredCoupons() {
      return this.availableCoupons
    },
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return '--'
      return dateString.split(' ')[0]
    },

    async show(e) {
      this.visible = true
      this.packageInfo = Object.assign({}, e)
      this.loading = false
      this.paymentSuccess = false
      this.paymentError = false
      this.errorMessage = ''
      this.selectedCoupon = null
      this.hasAutoSelectedCoupon = false
      this.qrCodeGenerated = false
      this.getConfigKey('CompanyPhone').then((response) => {
        this.initPassword = response.data
      })
      this.fetchAvailableCoupons(e.id)
    },

    async generateQRCode() {
      this.loading = true
      this.paymentError = false
      this.errorMessage = ''

      try {
        await this.createOrder(this.packageInfo)
        this.qrCodeGenerated = true
      } catch (error) {
      } finally {
        this.loading = false
      }
    },

    async regenerateQRCode() {
      this.loading = true
      this.paymentError = false
      this.errorMessage = ''

      this.clearPolling()
      this.clearCountdown()
      this.currentOrderId = null

      // 清除优惠券信息
      this.selectedCoupon = null
      this.hasAutoSelectedCoupon = false

      try {
        await this.createOrder(this.packageInfo)
      } catch (error) {
      } finally {
        this.loading = false
      }
    },

    fetchAvailableCoupons(packageId) {
      this.couponLoading = true
      const params = {
        whType: 30,
        totalAmount: this.packageInfo?.price || 0,
        status: 1,
        ids: packageId ? [packageId] : [],
      }

      getTenantAvailableCoupons(params)
        .then((response) => {
          this.availableCoupons = response.data || []
        })
        .catch(() => {
          this.availableCoupons = []
        })
        .finally(() => {
          this.couponLoading = false
        })
    },

    createOrder(e) {
      return new Promise((resolve, reject) => {
        const form = {
          chooseType: e.chooseType || 0,
          packageId: e?.id,
          payMethod: 0,
          payType: 'WECHAT',
          useCoupon: this.selectedCoupon ? 1 : 0,
          id: this.selectedCoupon?.id || null,
          couponCode: this.selectedCoupon?.id || null,
          couponId: this.selectedCoupon?.id || null,
          tenantId: e.tenantId || null,
        }

        const apiFunc = e.tenantId ? creatOrderqiye : creatOrder
        apiFunc(form)
          .then((response) => {
            this.clearPolling()
            this.clearCountdown()

            this.currentOrderId = response.data.id
            this.currentOrderNumber = response.data.payOrderNo || '--'
            this.downloadData.url = `${this.$payUrl}?orderId=${response.data.id}`
            this.startPollingPaymentStatus()
            this.startCountdown()
            resolve(response)
          })
          .catch((error) => {
            this.handleCreateOrderError(error)
            reject(error)
          })
      })
    },

    handleCreateOrderError(error) {
      this.paymentError = true
      this.qrCodeGenerated = false

      if (error.response && error.response.data) {
        const errorData = error.response.data
        if (errorData.message) {
          this.errorMessage = errorData.message
        } else if (errorData.msg) {
          this.errorMessage = errorData.msg
        } else if (errorData.error) {
          this.errorMessage = errorData.error
        } else {
          this.errorMessage = '创建订单失败，请检查输入参数'
        }
      } else if (error.message && error.message !== 'error') {
        this.errorMessage = error.message
      } else if (error.message === 'error') {
        this.errorMessage = '请求处理失败，请检查网络连接或联系管理员'
      } else {
        this.errorMessage = '网络异常，请稍后重试'
      }
    },

    retryCreateOrder() {
      this.generateQRCode()
    },

    hide() {
      this.visible = false
      this.paying = false
      this.loading = false
      this.paymentSuccess = false
      this.paymentError = false
      this.errorMessage = ''
      this.selectedCoupon = null
      this.hasAutoSelectedCoupon = false
      this.qrCodeGenerated = false
      this.clearCountdown()
      this.clearPolling()
      this.currentOrderId = null
      this.currentOrderNumber = null
    },

    handleCancel() {
      this.hide()
    },

    showCouponList() {
      this.couponDrawerVisible = true
      this.fetchAvailableCoupons(this.packageInfo?.id)
    },

    closeCouponDrawer() {
      this.couponDrawerVisible = false
    },

    selectCoupon(coupon) {
      if (coupon.status === 1) {
        const originalPrice = parseFloat(this.packageInfo?.price) || 0
        if (coupon.cashCondition > 0 && originalPrice < coupon.cashCondition) {
          this.$message.warning(`该优惠券需要订单金额满${coupon.cashCondition}元才能使用`)
          return
        }
        this.selectedCoupon = coupon
      }
    },

    isCouponAvailable(coupon) {
      const originalPrice = parseFloat(this.packageInfo?.price) || 0
      if (coupon.cashCondition > 0 && originalPrice < coupon.cashCondition) {
        return false
      }
      return true
    },

    clearCoupon() {
      this.selectedCoupon = null
      if (this.qrCodeGenerated) {
        this.$message.info('优惠券已清除，请重新生成支付二维码')
        this.qrCodeGenerated = false
        this.clearPolling()
        this.clearCountdown()
      }
    },

    async confirmCouponSelection() {
      this.closeCouponDrawer()
      if (this.qrCodeGenerated) {
        this.$message.info('优惠券已更新，请重新生成支付二维码')
        this.qrCodeGenerated = false
        this.clearPolling()
        this.clearCountdown()
      }
    },

    getCouponDisplayText(coupon) {
      if (coupon.couponType === 10) {
        return `减${coupon.reduceAmount}元`
      } else if (coupon.couponType === 20) {
        return `${(coupon.couponDiscount * 10).toFixed(1)}折`
      } else if (coupon.couponType === 30) {
        return '免单'
      }
      return '优惠券'
    },

    getWhTypeColor(type) {
      const colors = {
        0: 'default',
        10: 'blue',
        20: 'green',
        30: 'orange',
      }
      return colors[type] || 'default'
    },

    getWhTypeText(type) {
      const texts = {
        0: '通用',
        10: '商城',
        20: '维修',
        30: '全景套餐',
      }
      return texts[type] || '未知类型'
    },

    startPollingPaymentStatus() {
      this.checkPaymentStatus()
      this.pollingTimer = setInterval(() => {
        this.checkPaymentStatus()
      }, 3000)
    },

    checkPaymentStatus() {
      if (!this.currentOrderId) return

      checkPayStatus({ id: this.currentOrderId })
        .then((response) => {
          const status = response.data
          if (status) {
            this.handlePaymentSuccess()
          }
        })
    },

    clearPolling() {
      if (this.pollingTimer) {
        clearInterval(this.pollingTimer)
        this.pollingTimer = null
      }
    },

    handlePaymentSuccess() {
      this.paymentSuccess = true
      this.clearPolling()
      this.clearCountdown()
      this.$message.success('支付成功！服务已开通')
      setTimeout(() => {
        this.$emit('payment-success', {
          orderNumber: this.currentOrderNumber,
          amount: this.actualPrice,
          originalAmount: this.packageInfo?.price,
          couponAmount: this.discountAmount,
          id: this.selectedCoupon?.id,
          packageName: this.packageInfo?.name,
          capacity: this.packageInfo?.capacity,
          orderId: this.currentOrderId,
        })
        this.hide()
      }, 2000)
    },

    startCountdown() {
      let timeLeft = 300
      this.updateCountdown(timeLeft)
      this.timer = setInterval(() => {
        timeLeft--
        this.updateCountdown(timeLeft)
        if (timeLeft <= 0) {
          this.clearCountdown()
          this.clearPolling()
          this.qrCodeGenerated = false
          this.$message.warning('二维码已过期，请重新生成')
        }
      }, 1000)
    },

    updateCountdown(seconds) {
      const minutes = Math.floor(seconds / 60)
      const remainingSeconds = seconds % 60
      this.countdown = `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
    },

    clearCountdown() {
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
    },

    getValidityText(packageItem) {
      if (packageItem?.packageType === 3) {
        return '随套餐'
      }
      return `${packageItem?.validDays || 0} 天`
    },
  },

  beforeUnmount() {
    this.clearCountdown()
    this.clearPolling()
  },
}
</script>

<style scoped>
.payment-modal-content {
  padding: 8px;
}

.order-info {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
}

.order-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.order-item:last-child {
  margin-bottom: 0;
}

.order-label {
  color: #666;
  font-size: 14px;
}

.order-value {
  color: #333;
  font-size: 14px;
  font-weight: 500;
}

.coupon-section {
  display: flex;
  align-items: center;
  gap: 8px;
}

.coupon-btn {
  padding: 0;
  height: auto;
}

.clear-coupon-btn {
  padding: 0 4px;
  height: 20px;
  font-size: 12px;
}

.price-section {
  display: flex;
  align-items: center;
  gap: 8px;
}

.original-price {
  color: #999;
  font-size: 14px;
  text-decoration: line-through;
}

.order-price {
  color: #ff4d4f;
  font-size: 18px;
  font-weight: 600;
}

.discount-amount {
  color: #52c41a;
  font-size: 12px;
  background: #f6ffed;
  padding: 2px 6px;
  border-radius: 4px;
}

/* 生成二维码按钮区域 */
.generate-qr-section {
  text-align: center;
  margin-bottom: 24px;
  padding: 20px;
  border: 2px dashed #e8e8e8;
  border-radius: 8px;
  background: #fafafa;
}

.generate-qr-btn {
  height: 48px;
  font-size: 16px;
  padding: 0 32px;
}

.generate-tip {
  margin-top: 8px;
  font-size: 14px;
  color: #666;
}

.coupon-applied-tip {
  margin-top: 8px;
  font-size: 14px;
  color: #52c41a;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 二维码显示区域 */
.qr-code-section {
  text-align: center;
  margin-bottom: 24px;
}

.qr-code-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.qr-code-title {
  font-size: 16px;
  font-weight: 500;
  color: #333;
}

.regenerate-btn {
  font-size: 14px;
}

.qr-code-container {
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
}

.qr-code-placeholder {
  width: 200px;
  height: 200px;
  min-width: 200px;
  min-height: 200px;
  max-width: 200px;
  max-height: 200px;
  background: #f5f5f5;
  border: 2px dashed #d9d9d9;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.qr-code-image {
  text-align: center;
  width: 200px;
  height: 200px;
  min-width: 200px;
  min-height: 200px;
  max-width: 200px;
  max-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.qr-code-image ::v-deep canvas {
  width: 200px !important;
  height: 200px !important;
  min-width: 200px !important;
  min-height: 200px !important;
  max-width: 200px !important;
  max-height: 200px !important;
}

.qr-code-tip {
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
}

.time-count {
  color: #ff4d4f;
  font-weight: 500;
}

.coupon-info-tip {
  font-size: 14px;
  color: #666;
  margin-top: 8px;
}

.coupon-highlight {
  color: #52c41a;
  font-weight: 500;
}

.change-coupon-btn {
  margin-left: 8px;
  font-size: 12px;
  height: auto;
  padding: 0;
}

/* 错误状态区域 */
.error-section {
  text-align: center;
  margin-bottom: 24px;
  padding: 20px;
}

.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.error-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.error-text {
  font-size: 16px;
  color: #ff4d4f;
  font-weight: 500;
  margin-bottom: 8px;
}

.error-desc {
  font-size: 14px;
  color: #666;
  text-align: center;
  margin-bottom: 16px;
  max-width: 300px;
}

.retry-btn {
  height: 36px;
  font-size: 14px;
}

/* 成功状态区域 */
.success-section {
  text-align: center;
  margin-bottom: 24px;
  padding: 20px;
}

.payment-success {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.success-icon {
  width: 60px;
  height: 60px;
  background: #52c41a;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 30px;
  font-weight: bold;
  margin-bottom: 16px;
}

.success-text {
  font-size: 18px;
  color: #52c41a;
  font-weight: 500;
  margin-bottom: 8px;
}

.success-desc {
  font-size: 14px;
  color: #666;
}

.payment-tips {
  background: #f6ffed;
  border: 1px solid #b7eb8f;
  border-radius: 6px;
  padding: 12px;
}

.tip-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 8px;
}

.tip-item:last-child {
  margin-bottom: 0;
}

.tip-dot {
  color: #52c41a;
  font-size: 16px;
  margin-top: 2px;
}

.tip-item span:last-child {
  font-size: 12px;
  color: #666;
  line-height: 1.4;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #4f46e5;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 12px;
}

.loading-text {
  font-size: 14px;
  color: #666;
}

.coupon-drawer-content {
  padding: 0;
}

.coupon-stats {
  padding: 0;
}

.coupon-count-section {
  background: #fafafa;
}

.coupon-change-warning {
  margin-top: 8px;
  font-size: 12px;
  color: #faad14;
  display: flex;
  align-items: center;
}

.coupon-list {
  max-height: 500px;
  overflow-y: auto;
  padding: 16px;
}

.coupon-item {
  display: flex;
  align-items: center;
  padding: 16px;
  margin-bottom: 12px;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s;
  background: linear-gradient(135deg, #fff 0%, #fafafa 100%);
  position: relative;
}

.coupon-item:hover {
  border-color: #1890ff;
  box-shadow: 0 2px 8px rgba(24, 144, 255, 0.1);
}

.coupon-item-selected {
  border-color: #52c41a;
  background: linear-gradient(135deg, #f6ffed 0%, #e6f7ff 100%);
}

.coupon-item-used,
.coupon-item-expired,
.coupon-item-unavailable {
  opacity: 0.6;
  cursor: not-allowed;
  background: linear-gradient(135deg, #f5f5f5 0%, #f0f0f0 100%);
}

.coupon-left {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-right: 16px;
  min-width: 80px;
}

.coupon-amount {
  text-align: center;
  margin-bottom: 8px;
}

.coupon-value {
  display: flex;
  align-items: baseline;
  justify-content: center;
}

.currency {
  font-size: 14px;
  color: #ff4d4f;
  margin-right: 2px;
}

.amount {
  font-size: 24px;
  font-weight: 600;
  color: #ff4d4f;
}

.unit {
  font-size: 14px;
  color: #ff4d4f;
  margin-left: 2px;
}

.coupon-condition {
  font-size: 12px;
  color: #999;
  text-align: center;
}

.coupon-info {
  flex: 1;
  margin-right: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.coupon-main-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.coupon-name {
  font-size: 15px;
  font-weight: 600;
  color: #333;
  line-height: 1.3;
}

.coupon-subtitle {
  font-size: 13px;
  color: #666;
  line-height: 1.3;
  font-weight: 400;
}

.coupon-secondary-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-top: 8px;
  border-top: 1px solid #f0f0f0;
}

.coupon-validity {
  font-size: 12px;
  color: #999;
  line-height: 1.3;
}

.coupon-module {
  display: flex;
  align-items: center;
}

.coupon-check {
  width: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.coupon-status-tag {
  position: absolute;
  top: 8px;
  right: 8px;
}

.no-coupon {
  padding: 40px 0;
  text-align: center;
}

.coupon-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
}

.coupon-loading .loading-text {
  margin-top: 12px;
  color: #666;
}

/* 文字省略号样式 */
.text-ellipsis {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}

.coupon-name.text-ellipsis,
.coupon-subtitle.text-ellipsis {
  max-width: 200px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}
</style>
